// Cold-start handoff keeps the shared globe stage fixed while the overlay fades.
import { useEffect, useState } from "react";
import { LoadingGlobeStage } from "./loading/LoadingGlobeStage";
import { LoadingStatusCard } from "./loading/LoadingStatusCard";
import {
  loadingEnvironmentPhrase,
  loadingEnvironmentStyle,
  readLoadingEnvironment,
} from "./loading/loading-environment";

interface SplashScreenProps {
  isReady: boolean;
}

export default function SplashScreen({ isReady }: SplashScreenProps) {
  const [phase, setPhase] = useState<"idle" | "fade">("idle");
  const [unmounted, setUnmounted] = useState(false);
  const loadingContext = readLoadingEnvironment();

  useEffect(() => {
    if (!isReady) return;
    const fadeFrame = window.requestAnimationFrame(() => setPhase("fade"));
    const unmountTimer = window.setTimeout(() => setUnmounted(true), 820);
    return () => {
      window.cancelAnimationFrame(fadeFrame);
      window.clearTimeout(unmountTimer);
    };
  }, [isReady]);

  if (unmounted) return null;

  const isFading = phase === "fade";

  return (
    <div
      data-splash-screen="true"
      style={{
        ...loadingEnvironmentStyle(loadingContext),
        position: "fixed",
        inset: 0,
        zIndex: 2147483000,
        overflow: "hidden",
        background: "var(--fintheon-bg, #050402)",
        opacity: isFading ? 0 : 1,
        pointerEvents: isFading ? "none" : "all",
        transition: "opacity 680ms cubic-bezier(0.16, 1, 0.3, 1)",
      }}
    >
      <div style={environmentOverlayStyle} />
      <div style={scanlineStyle} />
      <LoadingGlobeStage
        phase={isFading ? "ready" : "auth"}
        style={{ opacity: isFading ? 0 : 0.94 }}
      />
      <div
        style={{
          position: "absolute",
          zIndex: 2,
          left: "50%",
          bottom: "max(5.25rem, calc(14svh + env(safe-area-inset-bottom)))",
          width: "min(25rem, calc(100vw - 3rem))",
          transition:
            "opacity 420ms cubic-bezier(0.16, 1, 0.3, 1), transform 420ms cubic-bezier(0.16, 1, 0.3, 1)",
          opacity: isFading ? 0 : 1,
          transform: isFading
            ? "translate(-50%, 0.75rem)"
            : "translate(-50%, 0)",
        }}
      >
        <LoadingStatusCard
          phrase={loadingEnvironmentPhrase(loadingContext)}
          bare
          loadingContext={loadingContext}
        />
      </div>
    </div>
  );
}

const scanlineStyle = {
  position: "absolute",
  inset: 0,
  background:
    "repeating-linear-gradient(0deg, rgba(255,255,255,0.035) 0 1px, transparent 1px 3px), radial-gradient(circle at center, transparent 0 42%, rgba(0,0,0,0.56) 78%)",
  mixBlendMode: "screen",
  opacity: 0.28,
  pointerEvents: "none",
} as const;

const environmentOverlayStyle = {
  position: "absolute",
  inset: 0,
  background: "var(--fintheon-loading-overlay)",
  opacity: 0.74,
  pointerEvents: "none",
} as const;
